// apps/frontend/admin-ui/src/pages/reports-reconcile.ts
import { AdminApiError, type AdminClient, type ReconcileReport, type ReportBasis } from '@apx/admin-sdk';
import { emptyState } from '../empty-state.js';
import { fmtDateTime, fmtMoney } from '../format.js';
import { toDecimalString } from '@apx/money';
import { exportXlsxWorkbook, type XlsxSheet } from '../xlsx.js';
import { showToast } from '../toast.js';
import {
  acceptStatusCell,
  BASIS_HINT,
  BASIS_LABEL,
  escapeHtml,
  parseBasis,
  REPORT_BASIS_STORAGE_KEY,
  REPORT_TZ_STORAGE_KEY,
  resolveBrowserTz,
  txIdCell,
  tzSelectOptions,
  wireTxIdCopy,
  zonedWallClockToUTC,
} from './reports-shared.js';

import { wireDatePickers } from '../date-picker.js';

export interface ReconcileDeps {
  client: AdminClient;
  integratorId: string;
}

/**
 * Client-side guard mirroring the server's `RECONCILE_ID_CAP`. Checked here so
 * an operator who pastes a whole month gets told what to do instead of a 400.
 */
const ID_CAP = 5000;

/**
 * Splits a pasted blob into transaction ids. Operators paste from wildly
 * different places — a spreadsheet column (newlines), a CSV cell run (commas),
 * a chat message (spaces) — so every plausible separator is accepted rather
 * than demanding one format. Surrounding quotes come from Excel copies.
 */
export function parseIds(raw: string): string[] {
  const parts = raw
    .split(/[\s,;]+/)
    .map((s) => s.replace(/^["']+|["']+$/g, '').trim())
    .filter(Boolean);
  return [...new Set(parts)];
}

/**
 * Reconcile tab — paste an integrator's transaction id list, get the three-way
 * split against apx for the same window.
 *
 * This exists because the manual version (two paginated tables, one pair of
 * eyes) is where discrepancies go to survive for weeks. The interesting output
 * is usually not "missing" at all but a matched row with `inWindow: false`: a
 * transaction both systems hold, filed under different days.
 */
export async function renderReconcileTab(
  root: HTMLElement,
  deps: ReconcileDeps,
  meta: { title: string; sub: string },
  games: string[],
): Promise<void> {
  const browserTz = resolveBrowserTz();
  const storedTz = localStorage.getItem(REPORT_TZ_STORAGE_KEY);
  const tzOptions = tzSelectOptions(browserTz);
  const initialTz = storedTz && tzOptions.some((o) => o.value === storedTz) ? storedTz : browserTz;
  const initialBasis = parseBasis(localStorage.getItem(REPORT_BASIS_STORAGE_KEY));

  root.innerHTML = `
    <h1 class="page-title">${escapeHtml(meta.title)}</h1>
    <p class="page-sub">${escapeHtml(meta.sub)}</p>

    <div class="card report-filters">
      <div class="filter-row">
        <label class="filter-field">Game
          <select id="rc-game" class="field-input">
            <option value="">All games</option>
            ${games.map((g) => `<option value="${escapeHtml(g)}">${escapeHtml(g)}</option>`).join('')}
          </select>
        </label>
        <label class="filter-field">Window
          <select id="rc-since" class="field-input">
            <option value="today">Today</option>
            <option value="yesterday" selected>Yesterday</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="custom">Custom range…</option>
          </select>
        </label>
        <label class="filter-field">Count by
          <select id="rc-basis" class="field-input">
            ${(['settled', 'placed'] as const)
              .map((b) => `<option value="${b}"${b === initialBasis ? ' selected' : ''}>${escapeHtml(BASIS_LABEL[b])}</option>`)
              .join('')}
          </select>
        </label>
        <label class="filter-field">Timezone
          <select id="rc-tz" class="field-input">
            ${tzOptions
              .map((o) => `<option value="${escapeHtml(o.value)}"${o.value === initialTz ? ' selected' : ''}>${escapeHtml(o.label)}</option>`)
              .join('')}
          </select>
        </label>
        <span id="rc-custom-range" class="filter-field-group hidden">
          <span class="filter-field"><label for="rc-from">From</label><input id="rc-from" type="datetime-local" step="1" class="field-input" /></span>
          <span class="filter-field"><label for="rc-to">To</label><input id="rc-to" type="datetime-local" step="1" class="field-input" /></span>
        </span>
        <span id="rc-msg" class="muted filter-msg"></span>
      </div>
      <div id="rc-range" class="filter-range muted"></div>
    </div>

    <div class="card">
      <h2>Their transaction ids</h2>
      <p class="muted section-desc">
        Paste the transaction id column from the integrator's report — one per line, or comma/space separated.
        Up to ${ID_CAP.toLocaleString()} ids. apx sends these same ids as the wallet call's <code>tx_id</code>, so they match verbatim.
      </p>
      <textarea id="rc-ids" class="field-input" rows="8" spellcheck="false"
        placeholder="1785270924642-3fziy6ct&#10;1785270835225-wlyssz2r&#10;…"></textarea>
      <div class="filter-row" style="margin-top:12px">
        <span id="rc-count" class="muted"></span>
        <button id="rc-run" class="primary">Compare</button>
        <button id="rc-export" class="ghost" disabled>Export Excel</button>
      </div>
    </div>

    <div id="rc-results"></div>
  `;

  const idsEl = root.querySelector<HTMLTextAreaElement>('#rc-ids')!;
  const countEl = root.querySelector<HTMLElement>('#rc-count')!;
  const msgEl = root.querySelector<HTMLElement>('#rc-msg')!;
  const rangeEl = root.querySelector<HTMLElement>('#rc-range')!;
  const resultsEl = root.querySelector<HTMLElement>('#rc-results')!;
  const exportBtn = root.querySelector<HTMLButtonElement>('#rc-export')!;
  const fromEl = root.querySelector<HTMLInputElement>('#rc-from')!;
  const toEl = root.querySelector<HTMLInputElement>('#rc-to')!;

  wireTxIdCopy(root, (id) => showToast(`Copied ${id}`, 'success'));
  wireDatePickers(root);

  let last: { report: ReconcileReport; tz: string } | null = null;

  function updateCount(): void {
    const n = parseIds(idsEl.value).length;
    countEl.textContent = n === 0 ? '' : `${n.toLocaleString()} unique id${n === 1 ? '' : 's'} pasted`;
    countEl.className = n > ID_CAP ? 'error' : 'muted';
  }
  idsEl.addEventListener('input', updateCount);

  function readWindow(): { from?: string; to?: string; since?: '24h' | '7d'; tz: string; basis: ReportBasis; error?: string } {
    const tz = root.querySelector<HTMLSelectElement>('#rc-tz')!.value || browserTz;
    const basis = parseBasis(root.querySelector<HTMLSelectElement>('#rc-basis')!.value);
    const sel = root.querySelector<HTMLSelectElement>('#rc-since')!.value;
    if (sel === 'custom') {
      if (!fromEl.value || !toEl.value) return { tz, basis, error: 'Pick both From and To for a custom range.' };
      const from = zonedWallClockToUTC(fromEl.value, tz);
      const to = zonedWallClockToUTC(toEl.value, tz);
      if (from >= to) return { tz, basis, error: 'From must be before To.' };
      return { from: from.toISOString(), to: to.toISOString(), tz, basis };
    }
    if (sel === 'today' || sel === 'yesterday') {
      // Whole calendar day in the SELECTED tz. The server's exclusive
      // next-second bound turns 23:59:59 into `< next-midnight`, so the day is
      // fully covered — the same construction the P/L report uses.
      const dayStr = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(
        new Date(Date.now() - (sel === 'yesterday' ? 86_400_000 : 0)),
      );
      return {
        from: zonedWallClockToUTC(`${dayStr}T00:00:00`, tz).toISOString(),
        to: zonedWallClockToUTC(`${dayStr}T23:59:59`, tz).toISOString(),
        tz,
        basis,
      };
    }
    return { since: sel as '24h' | '7d', tz, basis };
  }

  async function run(): Promise<void> {
    const ids = parseIds(idsEl.value);
    if (ids.length === 0) {
      msgEl.textContent = 'Paste at least one transaction id.';
      msgEl.className = 'error filter-msg';
      return;
    }
    if (ids.length > ID_CAP) {
      msgEl.textContent = `${ids.length.toLocaleString()} ids — narrow the window and compare at most ${ID_CAP.toLocaleString()} at a time.`;
      msgEl.className = 'error filter-msg';
      return;
    }
    const w = readWindow();
    if (w.error) {
      msgEl.textContent = w.error;
      msgEl.className = 'error filter-msg';
      return;
    }
    const gameId = root.querySelector<HTMLSelectElement>('#rc-game')!.value || undefined;

    msgEl.textContent = 'Comparing…';
    msgEl.className = 'muted filter-msg';
    resultsEl.innerHTML = '';
    try {
      const report = await deps.client.reconcileTransactions(deps.integratorId, {
        ids,
        ...(w.from ? { from: w.from } : {}),
        ...(w.to ? { to: w.to } : {}),
        ...(w.since ? { since: w.since } : {}),
        ...(gameId ? { gameId } : {}),
        basis: w.basis,
      });
      last = { report, tz: w.tz };
      exportBtn.disabled = false;
      rangeEl.innerHTML = `${w.from ? `${escapeHtml(fmtDateTime(w.from, w.tz))} → ${escapeHtml(fmtDateTime(w.to!, w.tz))}` : escapeHtml(w.since ?? '')} · ${escapeHtml(w.tz)} · counted by <strong>${escapeHtml(BASIS_LABEL[w.basis].toLowerCase())}</strong><br><span class="muted">${escapeHtml(BASIS_HINT[w.basis])}</span>`;
      resultsEl.innerHTML = renderResults(report, w.tz);
      msgEl.textContent = '';
    } catch (err) {
      const m = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.textContent = m;
      msgEl.className = 'error filter-msg';
      resultsEl.innerHTML = `<div class="card"><p class="error">${escapeHtml(m)}</p></div>`;
    }
  }

  root.querySelector<HTMLButtonElement>('#rc-run')!.addEventListener('click', () => void run());
  root.querySelector<HTMLSelectElement>('#rc-since')!.addEventListener('change', (ev) => {
    root.querySelector<HTMLElement>('#rc-custom-range')!.classList.toggle(
      'hidden',
      (ev.target as HTMLSelectElement).value !== 'custom',
    );
  });
  root.querySelector<HTMLSelectElement>('#rc-tz')!.addEventListener('change', (ev) => {
    localStorage.setItem(REPORT_TZ_STORAGE_KEY, (ev.target as HTMLSelectElement).value);
  });
  root.querySelector<HTMLSelectElement>('#rc-basis')!.addEventListener('change', (ev) => {
    localStorage.setItem(REPORT_BASIS_STORAGE_KEY, (ev.target as HTMLSelectElement).value);
  });
  exportBtn.addEventListener('click', () => {
    if (!last) return;
    exportWorkbook(last.report, last.tz, deps.integratorId);
  });
}

/** Matched rows dated outside the window — both systems hold these, on
 *  different days. Called out separately because they read as "missing" on
 *  either side's report while being nobody's missing transaction. */
function offWindow(r: ReconcileReport) {
  return r.matched.filter((m) => !m.inWindow);
}

function renderResults(r: ReconcileReport, tz: string): string {
  const inWindowCount = r.matched.length - offWindow(r).length;
  const off = offWindow(r);

  const tiles = `<div class="players-stats">
      ${statTile('Matched in window', inWindowCount, 'Present in both, dated the same day.')}
      ${statTile('Dated differently', off.length, 'Present in both, filed under different days.', off.length > 0 ? 'neg' : '')}
      ${statTile('Not found in apx', r.missingInApx.length, 'They have it, apx has no such bet.', r.missingInApx.length > 0 ? 'neg' : '')}
      ${statTile('Missing from their list', r.missingInIntegrator.length, 'apx has it in-window, their list does not.', r.missingInIntegrator.length > 0 ? 'neg' : '')}
    </div>
    <div class="card">
      <p class="muted" style="margin:0">
        Compared ${r.submitted.toLocaleString()} ids against ${r.windowCount.toLocaleString()} apx transactions in the window.
        ${r.windowCapped ? '<strong>Window truncated at the server cap — “missing from their list” is incomplete. Narrow the range.</strong>' : ''}
      </p>
    </div>`;

  return (
    tiles +
    section(
      'Dated differently',
      'Both systems hold these. apx dates them outside the window you asked for, so they look missing from one side without either side having lost anything.',
      off.length === 0
        ? emptyState({ icon: 'sparkle', title: 'No boundary mismatches', hint: 'Every matched transaction falls inside the window.' })
        : matchedTable(off, tz),
    ) +
    section(
      'Not found in apx',
      'Ids from their list with no matching bet under this integrator. A real discrepancy, or an id from a different integrator/provider.',
      r.missingInApx.length === 0
        ? emptyState({ icon: 'sparkle', title: 'apx has every id they sent' })
        : `<div class="data-table-wrap"><table class="data-table"><thead><tr><th>Transaction ID</th></tr></thead><tbody>${r.missingInApx
            .map((id) => `<tr>${txIdCell(id)}</tr>`)
            .join('')}</tbody></table></div>`,
    ) +
    section(
      'Missing from their list',
      'apx transactions inside the window that the pasted list did not contain. A VOID row with Accept status “cancelled” is usually expected here — the player undid the bet and their ledger nets the stake to zero — while “confirmed” on a VOID row is an admin refund worth raising with them.',
      r.missingInIntegrator.length === 0
        ? emptyState({ icon: 'sparkle', title: 'Their list covers every apx transaction in the window' })
        : `<div class="data-table-wrap"><table class="data-table">
            <thead><tr><th>Transaction ID</th><th>Settled</th><th>Placed</th><th>Game</th><th>Outcome</th><th>Accept status</th><th>Currency</th><th class="num">Stake</th><th class="num">Payout</th></tr></thead>
            <tbody>${r.missingInIntegrator
              .map(
                (m) => `<tr>
                  ${txIdCell(m.transactionId)}
                  <td>${m.settledAt ? fmtDateTime(m.settledAt, tz) : '<span class="muted">unsettled</span>'}</td>
                  <td>${fmtDateTime(m.placedAt, tz)}</td>
                  <td><code>${escapeHtml(m.gameId)}</code></td>
                  <td><span class="pill ${m.outcome === 'WIN' ? 'live' : m.outcome === 'VOID' ? 'warn' : ''}">${escapeHtml(m.outcome)}</span></td>
                  ${acceptStatusCell(m.acceptStatus)}
                  <td><code>${escapeHtml(m.currency)}</code></td>
                  <td class="num">${fmtMoney(m.creditAmount, m.currency)}</td>
                  <td class="num">${fmtMoney(m.creditPayout, m.currency)}</td>
                </tr>`,
              )
              .join('')}</tbody></table></div>`,
    )
  );
}

function matchedTable(rows: ReconcileReport['matched'], tz: string): string {
  return `<div class="data-table-wrap"><table class="data-table">
    <thead><tr><th>Transaction ID</th><th>Settled</th><th>Placed</th><th>Game</th><th>Player</th><th>Outcome</th><th>Accept status</th><th>Currency</th><th class="num">Stake</th><th class="num">Payout</th></tr></thead>
    <tbody>${rows
      .map(
        (m) => `<tr>
          ${txIdCell(m.transactionId)}
          <td>${m.settledAt ? fmtDateTime(m.settledAt, tz) : '<span class="muted">unsettled</span>'}</td>
          <td>${fmtDateTime(m.placedAt, tz)}</td>
          <td><code>${escapeHtml(m.gameId)}</code></td>
          <td>${escapeHtml(m.username ?? m.integratorPlayerRef)}</td>
          <td><span class="pill ${m.outcome === 'WIN' ? 'live' : m.outcome === 'VOID' ? 'warn' : ''}">${escapeHtml(m.outcome)}</span></td>
          ${acceptStatusCell(m.acceptStatus)}
          <td><code>${escapeHtml(m.currency)}</code></td>
          <td class="num">${fmtMoney(m.creditAmount, m.currency)}</td>
          <td class="num">${fmtMoney(m.creditPayout, m.currency)}</td>
        </tr>`,
      )
      .join('')}</tbody></table></div>`;
}

function section(title: string, desc: string, body: string): string {
  return `<div class="card">
    <h2>${escapeHtml(title)}</h2>
    <p class="muted section-desc">${escapeHtml(desc)}</p>
    ${body}
  </div>`;
}

/** Matches the P/L report's KPI card markup so the two read as one system.
 *  `tone` is only applied for a non-zero count — a green/red zero is noise. */
function statTile(label: string, value: number, hint: string, tone = ''): string {
  return `<div class="stat-card">
    <div class="stat-card-label">${escapeHtml(label)}</div>
    <div class="stat-card-value ${tone}">${value.toLocaleString()}</div>
    <div class="stat-card-sub">${escapeHtml(hint)}</div>
  </div>`;
}

function exportWorkbook(r: ReconcileReport, tz: string, integratorId: string): void {
  const sheets: XlsxSheet[] = [
    {
      name: 'Summary',
      rows: [
        { Metric: 'Integrator', Value: integratorId },
        { Metric: 'Counted by', Value: BASIS_LABEL[r.basis] },
        { Metric: 'Timezone', Value: tz },
        { Metric: 'Ids submitted', Value: r.submitted },
        { Metric: 'Matched in window', Value: r.matched.length - offWindow(r).length },
        { Metric: 'Dated differently', Value: offWindow(r).length },
        { Metric: 'Not found in apx', Value: r.missingInApx.length },
        { Metric: 'Missing from their list', Value: r.missingInIntegrator.length },
        { Metric: 'apx transactions in window', Value: r.windowCount },
        { Metric: 'Window truncated', Value: r.windowCapped ? 'YES — result incomplete' : 'no' },
      ],
      columns: ['Metric', 'Value'],
    },
    {
      name: 'Matched',
      rows: r.matched.map((m) => ({
        TransactionId: m.transactionId,
        InWindow: m.inWindow ? 'yes' : 'no',
        SettledAt: m.settledAt ? fmtDateTime(m.settledAt, tz) : '',
        PlacedAt: fmtDateTime(m.placedAt, tz),
        Game: m.gameId,
        Player: m.username ?? m.integratorPlayerRef,
        Outcome: m.outcome,
        AcceptStatus: m.acceptStatus,
        Currency: m.currency,
        Stake: toDecimalString(m.creditAmount, m.currency),
        Payout: toDecimalString(m.creditPayout, m.currency),
        Net: toDecimalString(m.net, m.currency),
        RoundId: m.roundId,
      })),
      columns: [
        'TransactionId', 'InWindow', 'SettledAt', 'PlacedAt', 'Game', 'Player',
        'Outcome', 'AcceptStatus', 'Currency', 'Stake', 'Payout', 'Net', 'RoundId',
      ],
    },
    {
      name: 'Not in apx',
      rows: r.missingInApx.map((id) => ({ TransactionId: id })),
      columns: ['TransactionId'],
    },
    {
      name: 'Not in their list',
      rows: r.missingInIntegrator.map((m) => ({
        TransactionId: m.transactionId,
        SettledAt: m.settledAt ? fmtDateTime(m.settledAt, tz) : '',
        PlacedAt: fmtDateTime(m.placedAt, tz),
        Game: m.gameId,
        Outcome: m.outcome,
        AcceptStatus: m.acceptStatus,
        Currency: m.currency,
        Stake: toDecimalString(m.creditAmount, m.currency),
        Payout: toDecimalString(m.creditPayout, m.currency),
        Net: toDecimalString(m.net, m.currency),
        RoundId: m.roundId,
      })),
      columns: [
        'TransactionId', 'SettledAt', 'PlacedAt', 'Game', 'Outcome', 'AcceptStatus',
        'Currency', 'Stake', 'Payout', 'Net', 'RoundId',
      ],
    },
  ];
  exportXlsxWorkbook(`apx-reconcile-${new Date().toISOString().slice(0, 10)}.xlsx`, sheets);
  showToast('Reconciliation exported.', 'success');
}
